import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { SalesConfigurationRepository } from '../repositories/sales-configuration.repository';
import {
  DEFAULT_SALES_DECIMAL_DIGITS,
  SalesConfiguration,
  SalesConfigurationCondition,
  SalesConfigurationDecimalDigits,
} from '../types/sales-configuration';

/**
 * Holds the per-session sales editor configuration (`GET /meta/configuration`).
 * Load it once when the document editor opens and call {@link reload} whenever
 * the sales organization (Vkforg) changes — `salesDiscounts` and
 * `decimalDigits` can both differ per organization, so consumers (the positions
 * grid columns, the document footer) read these signals and rebuild.
 */
@Injectable({ providedIn: 'root' })
export class SalesConfigurationService {
  private readonly repository = inject(SalesConfigurationRepository);
  private readonly injector = inject(Injector);

  private readonly _configuration = signal<SalesConfiguration | null>(null);
  readonly configuration = this._configuration.asReadonly();
  readonly isLoading = signal(false);

  /** Sales discount conditions → one editable discount column each. */
  readonly salesDiscounts = computed<SalesConfigurationCondition[]>(
    () => this._configuration()?.salesDiscounts ?? []
  );

  /** Decimal-digit settings; falls back to defaults until the config loads. */
  readonly decimalDigits = computed<SalesConfigurationDecimalDigits>(
    () => this._configuration()?.decimalDigits ?? DEFAULT_SALES_DECIMAL_DIGITS
  );

  async load(): Promise<SalesConfiguration | null> {
    this.isLoading.set(true);
    try {
      const result = await this.repository.load();
      this._configuration.set(result ?? null);
      return result ?? null;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Refetch after a sales-organization change (ids/labels/digits may differ). */
  reload(): Promise<SalesConfiguration | null> {
    return this.load();
  }
}
